import { defineStore } from 'pinia';
import type {
  ICartLine,
} from '~/modules/cart/types/cart-types';
import type {
  IAPICheckoutResponseData,
  IDeliveryOption,
  IOrderLine,
  IPaymentOption,
} from '~/modules/checkout/types/orders';
import convertCartLineToOrder from '~/modules/checkout/utils/ConvertCartLineToOrder';
import convertLineToProduct from '~/modules/checkout/utils/convertLineToProduct';
import { useCartStore } from '~/modules/cart/stores/cartStore';

export const useCheckoutStore = defineStore('checkoutStore', () => {
  const cartStore = useCartStore();

  const cartLines = reactive<ICartLine[]>([]);
  const orderLines = computed(() => cartLines.map(convertCartLineToOrder));
  const orderProducts = computed(() => cartLines.map((line) => convertLineToProduct(line)));
  const deliveryOptions = reactive<IDeliveryOption[]>([]);
  const paymentOptions = reactive<IPaymentOption[]>([]);

  const total = ref(0);
  const subtotal = ref(0);
  const quantity = ref(0);
  const discountPercent = ref(0);
  const discountPrice = ref(0);
  const availableBonusCount = ref(0);
  const accruedBonusCount = ref(0);
  const bonusAmount = ref(0);

  const {
    promocode,
    isPromocodeApplied,
  } = storeToRefs(cartStore);

  const initCheckoutLines = (lines: ICartLine[]) => {
    cartLines.length = 0;
    cartLines.push(...lines);
  };

  const initCheckoutDeliveries = (deliveries: IDeliveryOption[]) => {
    deliveryOptions.length = 0;
    deliveryOptions.push(...deliveries);
  };

  const initCheckoutPayments = (payments: IPaymentOption[]) => {
    paymentOptions.length = 0;
    paymentOptions.push(...payments);
  };

  const initCheckout = (data: IAPICheckoutResponseData) => {
    updateCheckoutCounters(data);
    initCheckoutLines(data.lines);

    if (data.deliveries) {
      initCheckoutDeliveries(data.deliveries);
    }

    if (data.payments) {
      initCheckoutPayments(data.payments);
    }
  };

  // todo: универсализировать с cartStore (или нет)
  const updateCheckoutCounters = (data: IAPICheckoutResponseData) => {
    if (data.total_amount) {
      total.value = Number(data.total_amount.amount);
    }

    if (data.subtotal_amount) {
      subtotal.value = Number(data.subtotal_amount.amount);
    }

    if (data.total_quantity) {
      quantity.value = Number(data.total_quantity.amount);
    }

    if (data.promo_code) {
      promocode.value = data.promo_code.value;
      isPromocodeApplied.value = true;
    } else if (data.promo_code === null) {
      isPromocodeApplied.value = false;
    }

    if (data.discount?.amount) {
      discountPrice.value = Number(data.discount.amount);
    } else {
      discountPrice.value = 0;
    }

    availableBonusCount.value = data.available_bonus_count ?? 0;
    accruedBonusCount.value = data.accrued_bonus_count ?? 0;
  };

  return {
    cartLines,
    orderLines,
    orderProducts,
    deliveryOptions,
    paymentOptions,
    total,
    subtotal,
    quantity,
    discountPercent,
    discountPrice,
    promocode,
    isPromocodeApplied,
    availableBonusCount,
    accruedBonusCount,
    bonusAmount,
    resetBonusAmount: () => { bonusAmount.value = 0; },
    initCheckout,
    initCheckoutLines,
    updateCheckoutCounters,
  };
});
